import React from 'react'

import { getConfigValue } from '../../lib/get-config-value'
import isLuna, { isLunaImport } from '../../lib/isLuna'
import { IConfigKey } from '../../lib/loadOcppConfigKeys'
import { KEY_LIGHT_INTENSITY } from '../constants/KeyLightIntensity'
import { OcppDimmerState } from '../constants/OcppDimmerStateEnum'
import { EarthingSchemeLB } from '../enums/earthingSchemes'
import {
    Phases,
    LUNA_PHASES_LIST,
    LUNA_PHASES_LIST_IT,
    PHASES_LIST_IT,
    PHASES_LIST_TN,
} from '../domain/electrical-settings'
import {
    ApiCustomer,
    ChargePoint,
    ChargePointImport,
    EarthingSystem,
    ExternalServerGetAllApiResponse,
} from '../redux/api/web-api'
import isAura from '../../lib/isAura'
import isDawn from '../../lib/isDawn'
import isHalo from '../../lib/isHalo'
import AuraIcon from '../../lib/icons/aura-icon/aura-icon'
import DawnIcon from '../../lib/icons/dawn-icon/dawn-icon'
import HaloIcon from '../../lib/icons/halo-icon/halo-icon'
import LunaIcon from '../../lib/icons/luna-icon/luna-icon'
import Text from '../../ui/Text'

export const transformCustomerData = (customerList: ApiCustomer[]) =>
    customerList
        .map((customer) => ({
            label: customer.callbackBaseUrl
                ? `[${customer.id}: ${customer.callbackBaseUrl}] ${customer.name}`
                : `[${customer.id}] ${customer.name}`,
            value: customer.id,
        }))
        .sort((a, b) => a.label.split('] ')[1].localeCompare(b.label.split('] ')[1]))

export const getDimmerValue = (configKeys: Array<IConfigKey>) => {
    const dimmerConfigValue = getConfigValue(KEY_LIGHT_INTENSITY, configKeys)
    if (!dimmerConfigValue || isNaN(+dimmerConfigValue)) return OcppDimmerState.OFF
    return dimmerConfigValue
}

export const getExternalServerLink = (id: number, externalServersList: ExternalServerGetAllApiResponse) =>
    externalServersList.find((server) => server?.id === +id)?.url

export const getChargePointWithLowestSupplyCurrent = (chargePoints: ChargePoint[]) =>
    chargePoints.reduce((acc: ChargePoint, val: ChargePoint) => {
        return val.supplyCurrent && val.supplyCurrent < (acc.supplyCurrent ?? 0) ? val : acc
    }, chargePoints[0])

const getPhaseList = (
    isLuna: boolean,
    earthingSystem: EarthingSystem | null | undefined,
    installationPhaseMode: string | null | undefined
) => {
    let phasesList: string[]
    if (isLuna) {
        phasesList = earthingSystem === EarthingSchemeLB.IT ? LUNA_PHASES_LIST_IT : LUNA_PHASES_LIST
    } else {
        phasesList = earthingSystem === EarthingSchemeLB.IT ? PHASES_LIST_IT : PHASES_LIST_TN
    }

    if (installationPhaseMode !== '3P') {
        return phasesList.filter((phase: string) => phase !== Phases.L123)
    }

    return phasesList
}

export const getChargerPhaseList = (
    chargePointProductId: number | undefined,
    earthingSystem: EarthingSystem | null | undefined,
    installationPhaseMode: string | null | undefined
) => {
    return getPhaseList(
        (chargePointProductId && isLuna(chargePointProductId)) as boolean,
        earthingSystem,
        installationPhaseMode
    )
}

export const getChargerPhaseListByImport = (
    chargePoint: ChargePointImport,
    earthingSystem: EarthingSystem | null | undefined,
    installationPhaseMode: string | null | undefined
) => {
    return getPhaseList(isLunaImport(chargePoint) as boolean, earthingSystem, installationPhaseMode)
}

export const getOwner = (chargePoint: ChargePoint) => {
    if (chargePoint?.ownerName) {
        return chargePoint?.ownerName
    }
    if (isAura(chargePoint) && chargePoint.connectors && chargePoint.connectors.length > 1) {
        const leftConnector = chargePoint.connectors[0]?.connectorUserName && (
            <div>
                <span className="font-bold mr-1">L:</span>
                <span className="overflow-ellipsis">{chargePoint.connectors[0]?.connectorUserName}</span>
            </div>
        )

        const rightConnector = chargePoint.connectors[1]?.connectorUserName && (
            <div>
                <span className="font-bold mr-1">R:</span>
                <span className="text-overflow-ellipsis">{chargePoint.connectors[1]?.connectorUserName}</span>
            </div>
        )

        return (
            <div className="flex flex-column">
                {leftConnector}
                {rightConnector}
            </div>
        )
    }
}

export const getChargerIcon = (chargePoint: ChargePoint, size?: number, className?: string) => {
    if (isAura(chargePoint))
        return (
            <span className={className}>
                <AuraIcon size={size} />
            </span>
        )
    if (isDawn(chargePoint))
        return (
            <span className={className}>
                <DawnIcon size={size} />
            </span>
        )
    if (isHalo(chargePoint))
        return (
            <span className={className}>
                <HaloIcon size={size} />
            </span>
        )
    if (isLuna(chargePoint))
        return (
            <span>
                <LunaIcon size={size} />
            </span>
        )
}

export const showErrorMessagesForIgnoreSchedule = (error: string) => {
    if (error === 'NoOngoingSession') return <Text code="NoOngoingSession" />
    if (error === 'NoScheduleForConnector') return <Text code="NoScheduleForConnector" />
    if (error === 'OverridingScheduleExists') return <Text code="OverridingScheduleExists" />
    if (error === 'OtherInvalid') return <Text code="msgIgnoreScheduleFailed" />
}
